/**
 * EPIC-046 part 2 phase F — extract the FIRST outbound http(s) URL
 * from server-rendered post HTML.
 *
 * The post HTML is server-sanitized via the safe-list renderer
 * (`renderMarkdownToSafeHtml`), so anchor tags are the only place
 * URLs surface. We pull the first <a href="..."> whose href is
 * absolute http(s), and skip same-origin links (those are platform
 * navigation, not outbound previews).
 *
 * Returns null if no eligible URL is present.
 */
export function extractFirstOutboundUrl(
  html: string,
  ownOrigins: string[] = [],
): string | null {
  const re = /<a\b[^>]*\bhref\s*=\s*"([^"]+)"/gi;
  let match;
  while ((match = re.exec(html)) !== null) {
    const raw = match[1];
    if (!raw) continue;
    if (!/^https?:\/\//i.test(raw)) continue;
    try {
      const u = new URL(raw);
      if (ownOrigins.includes(u.origin)) continue;
      return u.toString();
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * Plaintext variant — for surfaces (market comments, discussion
 * replies, anywhere prose isn't markdown-rendered) where URLs
 * appear bare. Same outbound-only / own-origin-skip semantics as
 * the HTML extractor; trailing punctuation that often follows
 * inline URLs ((),.;!?]) is trimmed because the URL parser would
 * otherwise treat it as part of the path.
 */
export function extractFirstPlainUrl(
  text: string,
  ownOrigins: string[] = [],
): string | null {
  const re = /\bhttps?:\/\/[^\s<>"'`]+/gi;
  let match;
  while ((match = re.exec(text)) !== null) {
    const raw = trimTrailingPunctuation(match[0]);
    try {
      const u = new URL(raw);
      if (ownOrigins.includes(u.origin)) continue;
      return u.toString();
    } catch {
      continue;
    }
  }
  return null;
}

function trimTrailingPunctuation(url: string): string {
  return url.replace(/[).,;!?\]]+$/, '');
}
